/**
 * BalanceTabModule -- Shared expense management integration.
 *
 * Shows group balances, recent transactions, and quick-add expense
 * within the AWENI shell. Links to /balancetab/ for full experience.
 */

import type { AweniContext, AweniModule } from '../../types';

export interface GroupBalance {
  groupId: number;
  groupName: string;
  balance: number;
  currency: string;
}

export interface Transaction {
  id: string;
  description: string;
  amount: number;
  currency: string;
  paidBy: string;
  date: string;
  groupId?: number;
}

interface BalanceTabData {
  stats: { groupCount: number; pendingAmount: number; totalManaged: number; currency: string };
  recentTransactions: Transaction[];
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return amount.toFixed(2) + ' ' + currency;
  }
}

function formatDate(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function getMockData(): BalanceTabData {
  return {
    stats: { groupCount: 3, pendingAmount: 45.20, totalManaged: 1234.56, currency: 'EUR' },
    recentTransactions: [
      { id: '1', description: 'Groceries', amount: 32.50, currency: 'EUR', date: new Date(Date.now() - 3600000).toISOString(), paidBy: 'You' },
      { id: '2', description: 'Restaurant', amount: 78.00, currency: 'EUR', date: new Date(Date.now() - 86400000).toISOString(), paidBy: 'Ahmed' },
      { id: '3', description: 'Gas', amount: 55.30, currency: 'EUR', date: new Date(Date.now() - 172800000).toISOString(), paidBy: 'Sara' },
      { id: '4', description: 'Utilities', amount: 120.00, currency: 'EUR', date: new Date(Date.now() - 259200000).toISOString(), paidBy: 'You' },
      { id: '5', description: 'Movie tickets', amount: 24.00, currency: 'EUR', date: new Date(Date.now() - 345600000).toISOString(), paidBy: 'Omar' },
    ],
  };
}

export default class BalanceTabModule implements AweniModule {
  private container: HTMLElement | null = null;

  async mount(container: HTMLElement, ctx: AweniContext): Promise<void> {
    this.container = container;
    const t = ctx.i18n.t.bind(ctx.i18n);
    const isRtl = ctx.i18n.dir === 'rtl';

    let data: BalanceTabData;
    try {
      data = await ctx.api.get<BalanceTabData>('/aweni/api/v1/balancetab/summary');
    } catch {
      data = getMockData();
    }

    const { stats, recentTransactions } = data;

    container.innerHTML = `
      <div class="balancetab-module px-4 pb-24 pt-2 max-w-3xl mx-auto" dir="${isRtl ? 'rtl' : 'ltr'}">

        <!-- Hero -->
        <section class="rounded-2xl p-6 mb-6 text-center" style="background: linear-gradient(135deg, #8B5CF6, #6D28D9);">
          <div class="w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center" style="background: rgba(255,255,255,0.2)">
            <svg class="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 10h20"/><path d="M6 16h4"/><path d="M14 16h4"/>
            </svg>
          </div>
          <h1 class="text-xl font-bold text-white mb-1">${t('balancetab_title')}</h1>
          <p class="text-sm text-white/80">${t('balancetab_subtitle')}</p>
        </section>

        <!-- Stats -->
        <section class="mb-6">
          <div class="grid grid-cols-3 gap-3">
            <div class="rounded-2xl p-4 text-center" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
              <p class="text-2xl font-bold" style="color: #8B5CF6">${stats.groupCount}</p>
              <p class="text-xs mt-1" style="color: var(--text-secondary)">${t('balancetab_groups')}</p>
            </div>
            <div class="rounded-2xl p-4 text-center" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
              <p class="text-2xl font-bold" style="color: #EF4444">${formatCurrency(stats.pendingAmount, stats.currency)}</p>
              <p class="text-xs mt-1" style="color: var(--text-secondary)">${t('balancetab_pending')}</p>
            </div>
            <div class="rounded-2xl p-4 text-center" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">
              <p class="text-2xl font-bold" style="color: #0A0A0A">${formatCurrency(stats.totalManaged, stats.currency)}</p>
              <p class="text-xs mt-1" style="color: var(--text-secondary)">${t('balancetab_total_managed')}</p>
            </div>
          </div>
        </section>

        <!-- Actions -->
        <section class="mb-6">
          <div class="grid grid-cols-2 gap-3">
            <button class="bt-action flex items-center gap-3 p-4 rounded-2xl transition-transform active:scale-95" style="background: var(--card-bg); box-shadow: var(--shadow-sm)" data-action="add-expense" aria-label="${t('balancetab_add_expense')}">
              <div class="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style="background: #8B5CF620; color: #8B5CF6">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v8M8 12h8"/></svg>
              </div>
              <span class="text-sm font-semibold" style="color: var(--text-primary)">${t('balancetab_add_expense')}</span>
            </button>
            <button class="bt-action flex items-center gap-3 p-4 rounded-2xl transition-transform active:scale-95" style="background: var(--card-bg); box-shadow: var(--shadow-sm)" data-action="groups" aria-label="${t('balancetab_view_groups')}">
              <div class="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style="background: #3B82F620; color: #3B82F6">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <span class="text-sm font-semibold" style="color: var(--text-primary)">${t('balancetab_view_groups')}</span>
            </button>
            <button class="bt-action flex items-center gap-3 p-4 rounded-2xl transition-transform active:scale-95" style="background: var(--card-bg); box-shadow: var(--shadow-sm)" data-action="settle" aria-label="${t('balancetab_settle_up')}">
              <div class="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style="background: #0A0A0A20; color: #0A0A0A">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <span class="text-sm font-semibold" style="color: var(--text-primary)">${t('balancetab_settle_up')}</span>
            </button>
            <button class="bt-action flex items-center gap-3 p-4 rounded-2xl transition-transform active:scale-95" style="background: var(--card-bg); box-shadow: var(--shadow-sm)" data-action="invite" aria-label="${t('balancetab_invite_member')}">
              <div class="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style="background: #F59E0B20; color: #F59E0B">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
              </div>
              <span class="text-sm font-semibold" style="color: var(--text-primary)">${t('balancetab_invite_member')}</span>
            </button>
          </div>
        </section>

        <!-- Recent Transactions -->
        <section class="mb-6">
          <h2 class="text-sm font-semibold mb-3 uppercase tracking-wide" style="color: var(--text-secondary)">${t('balancetab_recent_transactions')}</h2>
          ${recentTransactions.length > 0
            ? '<div class="rounded-2xl overflow-hidden" style="background: var(--card-bg); box-shadow: var(--shadow-sm)">'
              + recentTransactions.slice(0, 5).map((tx) =>
                '<div class="flex items-center justify-between px-4 py-3 border-b last:border-b-0" style="border-color: var(--border)">'
                + '<div class="flex items-center gap-3 min-w-0">'
                + '<div class="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style="background: var(--bg-secondary); color: var(--text-secondary)">'
                + '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 10h20"/></svg></div>'
                + '<div class="min-w-0"><p class="text-sm truncate" style="color: var(--text-primary)">' + tx.description + '</p>'
                + '<p class="text-xs" style="color: var(--text-secondary)">' + tx.paidBy + ' &middot; ' + formatDate(tx.date) + '</p></div></div>'
                + '<span class="text-sm font-semibold shrink-0 ms-3" style="color: var(--text-primary)">' + formatCurrency(tx.amount, tx.currency) + '</span></div>',
              ).join('')
              + '</div>'
            : '<p class="text-sm text-center py-6" style="color: var(--text-secondary)">' + t('balancetab_no_transactions') + '</p>'
          }
        </section>

        <!-- Open Full -->
        <section class="text-center">
          <a href="/balancetab/" class="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-transform active:scale-95" style="background: #8B5CF6; color: white;" target="_blank" rel="noopener" aria-label="${t('balancetab_open_full')}">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            ${t('balancetab_open_full')}
          </a>
        </section>
      </div>
    `;

    container.querySelectorAll<HTMLButtonElement>('.bt-action').forEach((btn) => {
      btn.addEventListener('click', () => {
        const routes: Record<string, string> = {
          'add-expense': '/balancetab/#/transactions/new',
          groups: '/balancetab/#/groups',
          settle: '/balancetab/#/settle',
          invite: '/balancetab/#/groups',
        };
        window.open(routes[btn.dataset.action || ''] || '/balancetab/', '_blank', 'noopener');
      });
    });
  }

  unmount(): void {
    if (this.container) {
      this.container.innerHTML = '';
      this.container = null;
    }
  }
}
